import { SizeSpacing4 } from '@snowball-tech/design-tokens/dist/web/typescript/design-tokens'
import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'

import { type ReactNode, createElement } from 'react'

import isEmpty from 'lodash/fp/isEmpty'

import {
  BTC_URL,
  CONTACT_EMAIL,
  HEY_EMAIL,
  LEGAL_NOTICE_DOC_URL,
  SNOWBALL_MAX_WHATSAPP_LINK,
  TOOLS_URL,
  USER_HOME_URL,
  USER_PROFILE_URL,
} from '../constants'
import {
  getHomePage,
  getPrivacyPage,
  getSnowballPlusPage,
} from '../helpers/navigation'
import { asPx } from '../styles/helpers'

export function getTags() {
  const landingPageUrl = getHomePage(true)
  const pricingPageUrl = getSnowballPlusPage(
    true,
    'edition_header_newsletter_description',
  )
  const privacyPageUrl = getPrivacyPage(true)

  return {
    b: (content: ReactNode) => createElement('b', {}, content),
    bold: (content: ReactNode) =>
      createElement(
        Typography,
        {
          element: 'span',
          variant: 'body-1-bold',
        },
        content,
      ),
    boldInline: (content: ReactNode) =>
      createElement(
        Typography,
        {
          element: 'span',
          inlineStyle: true,
          variant: 'body-1-bold',
        },
        content,
      ),
    boldInlineSmall: (content: ReactNode) =>
      createElement(
        Typography,
        {
          element: 'span',
          inlineStyle: true,
          variant: 'caption-bold',
        },
        content,
      ),
    boldInlineSmaller: (content: ReactNode) =>
      createElement(
        Typography,
        {
          element: 'span',
          inlineStyle: true,
          variant: 'body-2-bold',
        },
        content,
      ),
    boldSmall: (content: ReactNode) =>
      createElement(
        Typography,
        {
          element: 'span',
          variant: 'caption-bold',
        },
        content,
      ),
    boldSmaller: (content: ReactNode) =>
      createElement(
        Typography,
        {
          element: 'span',
          variant: 'body-2-bold',
        },
        content,
      ),

    median: (content: ReactNode) =>
      createElement(
        Typography,
        {
          element: 'span',
          variant: 'body-1-median',
        },
        content,
      ),
    medianSmall: (content: ReactNode) =>
      createElement(
        Typography,
        {
          element: 'span',
          variant: 'caption-median',
        },
        content,
      ),
    medianSmaller: (content: ReactNode) =>
      createElement(
        Typography,
        {
          element: 'span',
          variant: 'body-2-median',
        },
        content,
      ),

    normal: (content: ReactNode) =>
      createElement(
        Typography,
        {
          element: 'span',
          variant: 'body-1',
        },
        content,
      ),
    normalSmaller: (content: ReactNode) =>
      createElement(
        Typography,
        {
          element: 'span',
          variant: 'body-2',
        },
        content,
      ),

    i: (content: ReactNode) => createElement('i', {}, content),
    italic: (content: ReactNode) =>
      createElement(
        Typography,
        {
          className: 'italic',
          element: 'span',
        },
        content,
      ),

    small: (content: ReactNode) =>
      createElement(
        Typography,
        {
          element: 'span',
          variant: 'caption-median',
        },
        content,
      ),
    smaller: (content: ReactNode) =>
      createElement(
        Typography,
        {
          element: 'span',
          variant: 'body-2',
        },
        content,
      ),
    smallerInline: (content: ReactNode) =>
      createElement(
        Typography,
        {
          element: 'span',
          inlineStyle: true,
          variant: 'body-2',
        },
        content,
      ),
    smallInline: (content: ReactNode) =>
      createElement(
        Typography,
        {
          element: 'span',
          inlineStyle: true,
          variant: 'caption-median',
        },
        content,
      ),

    errorText: (content: ReactNode) =>
      createElement('span', { className: 'text-error' }, content),

    br: () => createElement('br'),

    li: (content: ReactNode) =>
      createElement(
        'li',
        {
          style: {
            margin: 0,
            marginLeft: asPx(SizeSpacing4, { withUnit: true }),
          },
        },
        content,
      ),
    ul: (content: ReactNode) =>
      createElement('ul', { style: { margin: 0, paddingLeft: 0 } }, content),

    pricingPage: (content: ReactNode) =>
      createElement(
        Typography,
        {
          href: pricingPageUrl,
          target: '_blank',
          variant: 'body-1-link',
        },
        content,
      ),
    pricingPageInline: (content: ReactNode) =>
      createElement(
        Typography,
        {
          disableClickTracking: true,
          href: pricingPageUrl,
          inlineStyle: true,
          target: '_blank',
          variant: 'body-1-link',
        },
        content,
      ),
    pricingPageInlineSmall: (content: ReactNode) =>
      createElement(
        Typography,
        {
          disableClickTracking: true,
          href: pricingPageUrl,
          inlineStyle: true,
          target: '_blank',
          variant: 'caption-link',
        },
        content,
      ),
    pricingPageInlineSmaller: (content: ReactNode) =>
      createElement(
        Typography,
        {
          disableClickTracking: true,
          href: pricingPageUrl,
          inlineStyle: true,
          target: '_blank',
          variant: 'body-2-link',
        },
        content,
      ),
    pricingPageSmall: (content: ReactNode) =>
      createElement(
        Typography,
        {
          href: pricingPageUrl,
          target: '_blank',
          variant: 'caption-link',
        },
        content,
      ),
    pricingPageSmaller: (content: ReactNode) =>
      createElement(
        Typography,
        {
          href: pricingPageUrl,
          inlineStyle: true,
          target: '_blank',
          variant: 'body-2-link',
        },
        content,
      ),

    privacyPage: (content: ReactNode) =>
      createElement(
        Typography,
        {
          href: privacyPageUrl,
          target: '_blank',
          variant: 'body-1-link',
        },
        content,
      ),
    privacyPageInline: (content: ReactNode) =>
      createElement(
        Typography,
        {
          disableClickTracking: true,
          href: privacyPageUrl,
          inlineStyle: true,
          target: '_blank',
          variant: 'body-1-link',
        },
        content,
      ),
    privacyPageInlineSmall: (content: ReactNode) =>
      createElement(
        Typography,
        {
          disableClickTracking: true,
          href: privacyPageUrl,
          inlineStyle: true,
          target: '_blank',
          variant: 'caption-link',
        },
        content,
      ),
    privacyPageInlineSmaller: (content: ReactNode) =>
      createElement(
        Typography,
        {
          disableClickTracking: true,
          href: privacyPageUrl,
          inlineStyle: true,
          target: '_blank',
          variant: 'body-2-link',
        },
        content,
      ),
    privacyPageSmall: (content: ReactNode) =>
      createElement(
        Typography,
        {
          href: privacyPageUrl,
          target: '_blank',
          variant: 'caption-link',
        },
        content,
      ),
    privacyPageSmaller: (content: ReactNode) =>
      createElement(
        Typography,
        {
          href: privacyPageUrl,
          target: '_blank',
          variant: 'body-2-link',
        },
        content,
      ),

    btcLink: (content: ReactNode) =>
      createElement(
        Typography,
        {
          href: BTC_URL,
          target: '_blank',
          variant: 'body-1-link',
        },
        content,
      ),
    heyContactLink: (content: ReactNode) =>
      createElement(
        Typography,
        {
          href: `mailto:${HEY_EMAIL}`,
          target: '_blank',
          variant: 'body-1-link',
        },
        content && !isEmpty(content.toString())
          ? content
          : decodeURIComponent(HEY_EMAIL),
      ),
    homePage: (content: ReactNode) =>
      createElement(
        Typography,
        {
          href: landingPageUrl,
          variant: 'body-1-link',
        },
        content,
      ),
    homePageSmall: (content: ReactNode) =>
      createElement(
        Typography,
        {
          href: landingPageUrl,
          variant: 'caption-link',
        },
        content,
      ),
    homePageSmaller: (content: ReactNode) =>
      createElement(
        Typography,
        {
          href: landingPageUrl,
          variant: 'body-2-link',
        },
        content,
      ),
    legalNoticeLink: (content: ReactNode) =>
      createElement(
        Typography,
        {
          href: LEGAL_NOTICE_DOC_URL,
          target: '_blank',
          variant: 'body-1-link',
        },
        content,
      ),
    mailContactLink: (content: ReactNode) =>
      createElement(
        Typography,
        {
          href: `mailto:${CONTACT_EMAIL}`,
          target: '_blank',
          variant: 'body-1-link',
        },
        content && !isEmpty(content.toString())
          ? content
          : decodeURIComponent(CONTACT_EMAIL),
      ),
    snowballLink: (content: ReactNode) =>
      createElement(
        Typography,
        {
          href: landingPageUrl,
          target: '_blank',
          variant: 'body-1-link',
        },
        content,
      ),

    list: (content: ReactNode) =>
      createElement('ul', { className: 'flex flex-col gap-2' }, content),
    listItem: (content: ReactNode) => createElement('li', {}, content),

    toolFinanceSpreadsheet: (nodeContent: ReactNode) => (
      <Typography
        href={TOOLS_URL.financeSpreadsheet}
        target="_blank"
        variant="body-1-link"
      >
        {nodeContent}
      </Typography>
    ),
    toolFinanceSpreadsheetSmaller: (nodeContent: ReactNode) => (
      <Typography
        href={TOOLS_URL.financeSpreadsheet}
        target="_blank"
        variant="body-2-link"
      >
        {nodeContent}
      </Typography>
    ),

    toolInvestTracker: (nodeContent: ReactNode) => (
      <Typography
        href={TOOLS_URL.investTracker}
        target="_blank"
        variant="body-1-link"
      >
        {nodeContent}
      </Typography>
    ),
    toolInvestTrackerSmaller: (nodeContent: ReactNode) => (
      <Typography
        href={TOOLS_URL.investTracker}
        target="_blank"
        variant="body-2-link"
      >
        {nodeContent}
      </Typography>
    ),

    userHome: (nodeContent: ReactNode) => (
      <Typography href={USER_HOME_URL} variant="body-1-link">
        {nodeContent}
      </Typography>
    ),
    userHomeSmaller: (nodeContent: ReactNode) => (
      <Typography href={USER_HOME_URL} variant="body-2-link">
        {nodeContent}
      </Typography>
    ),

    myAccount: (nodeContent: ReactNode) => (
      <Typography href={USER_PROFILE_URL} variant="body-1-link">
        {nodeContent}
      </Typography>
    ),
    myAccountSmall: (nodeContent: ReactNode) => (
      <Typography href={USER_PROFILE_URL} variant="caption-link">
        {nodeContent}
      </Typography>
    ),
    myAccountSmaller: (nodeContent: ReactNode) => (
      <Typography href={USER_PROFILE_URL} variant="body-2-link">
        {nodeContent}
      </Typography>
    ),

    whatsapp: (nodeContent: ReactNode) => (
      <Typography
        href={SNOWBALL_MAX_WHATSAPP_LINK}
        target="_blank"
        variant="body-1-link"
      >
        {nodeContent}
      </Typography>
    ),
    whatsappSmaller: (nodeContent: ReactNode) => (
      <Typography
        href={SNOWBALL_MAX_WHATSAPP_LINK}
        target="_blank"
        variant="body-2-link"
      >
        {nodeContent}
      </Typography>
    ),
  }
}

type Props = {
  children(tags: Record<string, (chunks: ReactNode) => ReactNode>): ReactNode
}
export default function RichText({ children }: Props) {
  return children(getTags())
}
